(() => {
  const selector = 'slider-component[data-aperlamiri-content-slider]';
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const desktopBreakpoint = window.matchMedia('(min-width: 990px)');

  const getTrack = (component) => component.slider || component.querySelector('[id^="Slider-"]');
  const getItems = (component) =>
    component.sliderItemsToShow || Array.from(component.querySelectorAll('[id^="Slide-"]')).filter((item) => item.clientWidth > 0);

  const isSliderEnabled = (component) => {
    const track = getTrack(component);
    const enabled = desktopBreakpoint.matches
      ? component.dataset.aperlamiriDesktopSlider === 'true' && track?.classList.contains('slider--desktop')
      : component.dataset.aperlamiriMobileSlider === 'true' && track?.classList.contains('slider--tablet');

    return Boolean(enabled && track?.classList.contains('slider'));
  };

  const canScroll = (track) => track && track.scrollWidth > track.clientWidth + 1;

  const getItemsPerPage = (track, items, itemOffset) => {
    const columnsPattern = window.matchMedia('(min-width: 990px)').matches
      ? /grid--(\d+)-col-desktop/
      : /grid--(\d+)-col-tablet-down/;
    const columnsClass = Array.from(track.classList).find((className) => columnsPattern.test(className));
    const configuredColumns = Number(columnsClass?.match(columnsPattern)?.[1]);
    if (configuredColumns) return Math.min(items.length, configuredColumns);

    return Math.max(1, Math.floor(track.clientWidth / itemOffset));
  };

  const getPageMetrics = (component) => {
    const track = getTrack(component);
    const items = getItems(component);
    if (!track || items.length < 2) return { current: 1, items, itemsPerPage: 0, total: 0 };
    const itemOffset = items[1].offsetLeft - items[0].offsetLeft;
    if (!itemOffset) return { current: 1, items, itemsPerPage: 0, total: 0 };

    const itemsPerPage = getItemsPerPage(track, items, itemOffset);
    const pageWidth = itemOffset * itemsPerPage;
    const total = Math.ceil(items.length / itemsPerPage);
    const current = Math.round(track.scrollLeft / pageWidth) + 1;
    return { current: Math.min(Math.max(current, 1), total), items, itemsPerPage, total };
  };

  const makeButton = (page) => {
    const button = document.createElement('button');
    button.className = 'aperlamiri-slider-pagination__button';
    button.dataset.page = page;
    button.type = 'button';
    button.setAttribute('aria-label', `Go to page ${page}`);
    return button;
  };

  const initialize = (component) => {
    if (component.dataset.aperlamiriPaginationInitialized) return;
    const track = getTrack(component);
    if (!track) return;

    component.dataset.aperlamiriPaginationInitialized = 'true';
    let pagination;
    let interactionsBound = false;

    const updatePageSnapPoints = ({ items, itemsPerPage = 0 }) => {
      items.forEach((item, index) => {
        item.toggleAttribute('data-aperlamiri-page-start', itemsPerPage > 0 && index % itemsPerPage === 0);
      });
    };

    const onPaginationClick = (event) => {
      const button = event.target.closest('button[data-page]');
      if (!button) return;
      const { items, itemsPerPage } = getPageMetrics(component);
      const item = items[(Number(button.dataset.page) - 1) * itemsPerPage];
      if (!item) return;
      track.scrollTo({ left: item.offsetLeft, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
    };

    const bindInteractions = () => {
      if (interactionsBound) return;
      component.addEventListener('slideChanged', refresh);
      track.addEventListener('scroll', refresh, { passive: true });
      interactionsBound = true;
    };

    const teardown = () => {
      updatePageSnapPoints({ items: getItems(component) });
      if (interactionsBound) {
        component.removeEventListener('slideChanged', refresh);
        track.removeEventListener('scroll', refresh);
        interactionsBound = false;
      }
      if (pagination) {
        pagination.removeEventListener('click', onPaginationClick);
        pagination.remove();
        pagination = undefined;
      }
    };

    const createPagination = () => {
      pagination = document.createElement('div');
      pagination.className = 'aperlamiri-slider-pagination';
      pagination.setAttribute('aria-label', 'Carousel pagination');
      pagination.setAttribute('role', 'group');
      pagination.addEventListener('click', onPaginationClick);
      component.append(pagination);
    };

    const refresh = () => {
      if (!isSliderEnabled(component) || !canScroll(track)) {
        teardown();
        return;
      }

      if (!pagination) createPagination();
      bindInteractions();
      const metrics = getPageMetrics(component);
      const { current, total } = metrics;
      updatePageSnapPoints(metrics);
      pagination.hidden = total <= 1;
      if (pagination.childElementCount !== total) {
        pagination.replaceChildren(...Array.from({ length: total }, (_, index) => makeButton(index + 1)));
      }
      pagination.querySelectorAll('button').forEach((button, index) => {
        const active = index + 1 === current;
        button.toggleAttribute('aria-current', active);
        if (active) button.setAttribute('aria-current', 'page');
      });
    };

    const resizeObserver = new ResizeObserver(refresh);
    const cleanup = (event) => {
      if (!event.target.contains(component)) return;
      teardown();
      resizeObserver.disconnect();
      window.removeEventListener('resize', refresh);
      document.removeEventListener('shopify:section:unload', cleanup);
      delete component.dataset.aperlamiriPaginationInitialized;
    };

    document.addEventListener('shopify:section:unload', cleanup);
    window.addEventListener('resize', refresh, { passive: true });
    resizeObserver.observe(track);
    requestAnimationFrame(refresh);
  };

  const initializeAll = (root = document) => {
    if (root.matches?.(selector)) initialize(root);
    root.querySelectorAll?.(selector).forEach(initialize);
  };

  document.addEventListener('DOMContentLoaded', () => initializeAll());
  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
  new MutationObserver((mutations) => {
    mutations.forEach((mutation) => mutation.addedNodes.forEach((node) => node.nodeType === 1 && initializeAll(node)));
  }).observe(document.body, { childList: true, subtree: true });
})();
